import { UntypedFormGroup } from '@angular/forms';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of, switchMap, tap, map } from 'rxjs';
import { isMoment } from 'moment';
import { StackingPlanService } from '../../../../../stacking-plan.service';
import { AbstractSpaceClass } from '../../../../../models/space/abstract-space.class';
import { isTenantEntityActive } from '../../../../../shared/utils/functions';
import { ActionsToolbarService } from '../../../../actions-toolbar/actions-toolbar.service';
import { OPTIONS, SPRequest } from '../details-overlay/details-overlay.model';
import { entityParamsOverlay } from '../details-overlay/details-overlay.constants';
import { SuiteType } from '../item.model';
import { checkIfFloorNumberChange, formatData, getRequestEntityType } from './details-modal.constants';
import { ICanSaveSPSpace, SaveSpaceEvent, SaveSpaceOptions } from './space-save.service';

@Injectable()
export class DefaultSpaceSaveService implements ICanSaveSPSpace {
  constructor(
    protected stackingPlanService: StackingPlanService,
    protected actionsToolbarService: ActionsToolbarService
  ) {}

  public save(args: SaveSpaceOptions): Observable<SaveSpaceEvent> {
    const { space, floors, form } = args;
    const floor = floors.find(f => f.floor_number === space.floorNumber);
    if (!floor) {
      throw Error('Floor Number not Found!');
    }

    const originalSpaceSize = space.getAreaSpace();
    const { spaceSize, spaceSizeUOM } = form.value.formGroupSpace;
    const { suiteNumber } = form.value;
    const remainingSpace = originalSpaceSize > Number(spaceSize) ? originalSpaceSize - Number(spaceSize) : 0;

    // * Side effect of dynamic form on subset without dataField value...
    // Get Raw Value is needed to get data of disabled formcontrols as well because
    // lease type will be disabled when it doesn't meet criteria.
    const spFormValue = this._getFormData(form);

    const { floorId, floorNumber } = checkIfFloorNumberChange(
      floors,
      floor.floor_id,
      floor.floor_number,
      spFormValue.floorNumber
    );

    const requestParams = entityParamsOverlay(
      space,
      spFormValue.formGroupSpace?.spaceSizeUOM,
      floorNumber,
      this.stackingPlanService.propertyId,
      this.getPropertyUsage(space)?.id ?? 0,
      floorId
    );

    const spRequestObj = this._makeSPRequestObj(space, spFormValue, requestParams);

    return this.stackingPlanService.alertNonTrustedUser().pipe(
      switchMap(canContinue =>
        canContinue
          ? this.stackingPlanService.alertDuplicateSuite(floor, suiteNumber, space.suiteType, space.suiteId)
          : of(false)
      ),
      switchMap(canContinue =>
        canContinue
          ? this._doSave(space, spRequestObj, floors).pipe(map(result => new SaveSpaceEvent('completed', result)))
          : of(new SaveSpaceEvent('error', false))
      ),
      tap(event => {
        if (event.status === 'completed' && remainingSpace > 0) {
          const onSaveOptions = {
            remainingSpace,
            remainingSpaceUOM: spaceSizeUOM,
            floor: space.floorNumber,
            suiteNumber: suiteNumber,
          };
          this.stackingPlanService.setNewAvailabityTenancyCreation(onSaveOptions);
        }
      })
    );
  }

  protected _doSave = (
    space: AbstractSpaceClass,
    spRequestObj: SPRequest,
    floors?: Record<string, any>[]
  ): Observable<any> => {
    const palyloads$: Observable<any>[] = Object.entries(spRequestObj).reduce((acc, [key, value]) => {
      const { entity, edpMethod, errorMessage, reqClass, entityKey } = OPTIONS[key];
      const { payloads } =
        key === 'leaseSpace' ? new reqClass(value, edpMethod, floors) : new reqClass(value, edpMethod);

      return [
        ...acc,
        this.actionsToolbarService
          .updateOverlapFormData(payloads, entity, space[entityKey])
          .pipe(this.actionsToolbarService.errorHandling(errorMessage)),
      ];
    }, []);

    return forkJoin(palyloads$);
  };

  protected _getFormData = (form: UntypedFormGroup): Record<string, any> => {
    const rawData = form.getRawValue();
    if (!form.get('notes')?.dirty) {
      delete rawData.notes;
    }
    // TODO: check why this "undefined" is being added to the object and remove it
    delete rawData.undefined;
    return rawData;
  };

  protected _makeSPRequestObj(space, spFormValue, requestParams) {
    return Object.entries(spFormValue).reduce((acc, [key, value]) => {
      const isValueObj = value && typeof value === 'object' && !isMoment(value);
      const subValues = isValueObj ? Object.entries(value) : null;
      const field = isValueObj ? subValues[0][0] : key;
      const fieldValue = isValueObj ? subValues[0][1] : value;
      const unit = isValueObj ? subValues[1][0] : null;
      const unitValue = isValueObj ? subValues[1][1] : null;
      const entity = getRequestEntityType(field, space.suiteType);

      return {
        ...acc,
        [entity]: {
          ...requestParams[entity],
          ...acc[entity],
          [field]: formatData(fieldValue),
          ...(unit ? { [unit]: unitValue } : {}),
        },
      };
    }, {}) as SPRequest;
  }

  protected getPropertyUsage = (space: Record<string, any>): Record<string, any> => {
    if (space.suiteType === SuiteType.Availability) {
      return this.stackingPlanService.getPropertyUsage(space.refPropertyUsageTypeDesc);
    }
    const entity = !isTenantEntityActive() && space.suiteType === SuiteType.Tenant ? 'lease' : 'tenant';
    return this.stackingPlanService.getPropertyUsage(space[entity].ref_property_usage_type_desc);
  };
}
